<template>
  <v-row>
    <v-col cols="8" sm="8" md="8" lg="8" class="mt-0 pt-0">
      <v-text-field
        v-model="service.short_task"
        :label="$t('components.miscellaneous.serviceInterval.shortDescription')"
        color="primary"
        data-cy="serviceProtocolShortDescriptionInput"
      />
    </v-col>

    <v-col cols="4" sm="4" md="4" lg="4" class="mt-0 pt-0">
      <v-btn
        text
        color="primary"
        class="float-right mt-5"
        x-small
        @click="removeServiceProtocol(service)"
      >
        <v-icon color="red" class="mx-1">mdi-delete</v-icon>
      </v-btn>
      <v-btn
        text
        color="primary"
        class="float-right mt-5"
        x-small
        @click="showDetails = !showDetails"
        data-cy="serviceProtocolShowDetailsBtn"
      >
        <v-icon color="primary" class="mx-1" v-if="!showDetails"
          >mdi-eye-outline</v-icon
        >
        <v-icon color="primary" class="mx-1" v-else>mdi-eye-off-outline</v-icon>
      </v-btn>
    </v-col>
    <v-col
      cols="12"
      sm="12"
      md="12"
      lg="12"
      v-show="showDetails"
      class="mt-0 pt-0"
    >
      <label
        >{{
          $t("components.miscellaneous.serviceInterval.serviceInstructionsList")
        }}
      </label>
      <service-protocol-description
        :builder="true"
        :data.sync="service.long_task"
        :subtasks.sync="service.subtasks"
        :key="key"
      ></service-protocol-description>
    </v-col>
    <v-col cols="12" v-show="showDetails" class="mt-0 pt-0">
      <v-row v-for="interval in service.intervals" v-bind:key="interval.uuid">
        <v-col cols="5" sm="5" md="5" lg="5">
          <v-select
            v-model="interval.source"
            :items="sourceOptions"
            item-text="label"
            item-value="value"
            :label="
              $t('components.miscellaneous.serviceInterval.activityInterval')
            "
            color="primary"
            data-cy="serviceProtocolActivityIntervalInput"
          >
            <template v-slot:item="{ parent, item, on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-row class="d-flex px-2 justify-space-between">
                  {{ item.label }}
                  <v-tooltip v-if="item.hint !== null" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">
                        mdi-information
                      </v-icon>
                    </template>
                    <span>{{ item.hint }}</span>
                  </v-tooltip>
                </v-row>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
        <v-col cols="3" sm="3" md="3" lg="3">
          <v-select
            v-if="interval.source !== 'MANUAL'"
            v-model="interval.periodic"
            :items="periodicOptions"
            item-text="label"
            item-value="value"
            :label="$t('components.miscellaneous.serviceInterval.period')"
            color="primary"
            data-cy="serviceProtocolPeriodInput"
          />
        </v-col>
        <v-col cols="2" sm="2" md="2" lg="2">
          <v-text-field
            v-if="interval.source !== 'MANUAL'"
            v-model="interval.value"
            :label="serviceAmountLabel(interval.source)"
            type="number"
            color="primary"
            data-cy="serviceProtocolValueInput"
          />
        </v-col>
        <v-col cols="2" sm="2" md="2" lg="2">
          <v-btn
            class="mt-4 mr-2 float-right"
            icon
            @click="removeInterval(interval)"
          >
            <v-icon color="red">mdi-delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3" sm="3" md="3" lg="3">
          <v-btn
            class="mt-0"
            text
            color="primary"
            small
            @click="addNewInterval"
            data-cy="serviceProtocolNewIntervalBtn"
          >
            <v-icon class="mr-2" color="primary">mdi-plus</v-icon>
            {{ $t("components.miscellaneous.serviceInterval.newInterval") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import ServiceProtocolDescription from "./ServiceProtocolDescription";
export default {
  name: "ServiceInterval",
  props: ["service", "index"],
  components: {
    ServiceProtocolDescription,
  },
  data: () => ({
    showDetails: true,
    key: 0,
    sourceOptions: [],
    periodicOptions: [],
  }),
  created() {
    //when user add new service protocol rest of them should be closed
    this.setInitalData();
    if (this.index === 0) {
      this.showDetails = true;
    } else {
      this.showDetails = false;
    }
  },
  watch: {
    service(newValue, oldValue) {
      //VUEBUG https://github.com/vuejs/vue/issues/2024 :this is here to force refresh a component
      this.key++;
    },
  },
  methods: {
    setInitalData() {
      this.sourceOptions = [
        {
          value: "ENGINE_HOURS",
          hint: null,
          label: this.$t(
            "components.miscellaneous.serviceInterval.engineHoursHours"
          ),
        },
        {
          value: "ITEM_AGE_MONTHS",
          hint: null,
          label: this.$t(
            "components.miscellaneous.serviceInterval.itemAgeMonths"
          ),
        },
        {
          value: "MILEAGE",
          hint: this.$t(
            "components.miscellaneous.serviceInterval.servicingIntervalsOfTypeCurrentlyDoNotAppearInMaintenanceForecasts",
            {
              label: this.$t(
                "components.miscellaneous.serviceInterval.boatMileageNM"
              ),
            }
          ),
          label: this.$t(
            "components.miscellaneous.serviceInterval.boatMileageNM"
          ),
        },
        {
          value: "SAILING_HOURS",
          hint: this.$t(
            "components.miscellaneous.serviceInterval.servicingIntervalsOfTypeCurrentlyDoNotAppearInMaintenanceForecasts",
            {
              label: this.$t(
                "components.miscellaneous.serviceInterval.sailingHoursHours"
              ),
            }
          ),
          label: this.$t(
            "components.miscellaneous.serviceInterval.sailingHoursHours"
          ),
        },
        {
          value: "RUNNING_HOURS",
          hint: this.$t(
            "components.miscellaneous.serviceInterval.servicingIntervalsOfTypeCurrentlyDoNotAppearInMaintenanceForecasts",
            {
              label: this.$t(
                "components.miscellaneous.serviceInterval.runningHours"
              ),
            }
          ),
          label: this.$t(
            "components.miscellaneous.serviceInterval.runningHours"
          ),
        },
        {
          value: "MANUAL",
          hint: this.$t(
            "components.miscellaneous.serviceInterval.servicingIntervalsOfTypeCurrentlyDoNotAppearInMaintenanceForecasts",
            {
              label: this.$t("components.miscellaneous.serviceInterval.custom"),
            }
          ),
          label: this.$t("components.miscellaneous.serviceInterval.custom"),
        },
      ];
      this.periodicOptions = [
        {
          value: true,
          label: this.$t("components.miscellaneous.serviceInterval.every"),
        },
        {
          value: false,
          label: this.$t("components.miscellaneous.serviceInterval.at"),
        },
      ];
    },
    serviceAmountLabel(val) {
      switch (val) {
        case "ENGINE_HOURS":
          return this.$t("components.miscellaneous.serviceInterval.hours");
        case "SAILING_HOURS":
          return this.$t("components.miscellaneous.serviceInterval.hours");
        case "RUNNING_HOURS":
          return this.$t("components.miscellaneous.serviceInterval.hours");
        case "ITEM_AGE_MONTHS":
          return this.$t("components.miscellaneous.serviceInterval.months");
        default:
          return this.$t("components.miscellaneous.serviceInterval.nm");
      }
    },
    addNewInterval(event) {
      this.service.intervals.push(this.intervalTemplate());
    },
    removeInterval(interval) {
      this.service.intervals.splice(
        this.service.intervals.indexOf(interval),
        1
      );
    },
    removeServiceProtocol(service) {
      this.$emit("delete-service-protocol");
    },
    intervalTemplate: () => {
      return {
        periodic: true,
        source: "ENGINE_HOURS",
        value: 0,
      };
    },
  },
};
</script>

<style scoped></style>
